import { Link } from "react-router-dom";
import { Chip, colors, fonts, radii, spacing } from "@edufadder/ui";
import * as s from "../styles.js";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section id={id} aria-labelledby={`${id}-h`} style={{ scrollMarginTop: 90 }}>
      <h2 id={`${id}-h`} style={s.sectionTitle}>
        {title}
      </h2>
      <hr style={s.goldRule} />
      <div style={s.prose}>{children}</div>
    </section>
  );
}

const TOC: Array<[string, string]> = [
  ["what", "What EduFadder is"],
  ["start", "Getting started"],
  ["browse", "While you browse and read"],
  ["notes", "Reading a research note"],
  ["search", "While you search"],
  ["anything", "Checking anything"],
  ["control", "Staying in control"],
  ["privacy", "Your privacy"],
  ["improve", "Help improve EduFadder"],
  ["faq", "Questions"],
];

export function GuidePage(): JSX.Element {
  return (
    <div style={{ ...s.wide, maxWidth: 940 }}>
      <h1 style={{ ...s.heading1, fontSize: 34 }}>How to use EduFadder</h1>
      <hr style={s.goldRule} />
      <p style={{ ...s.lead, maxWidth: 760 }}>
        A short guide for teachers, students, parents, and anyone who reads about education online.
      </p>
      <nav aria-label="On this page" style={{ ...s.card, display: "flex", flexWrap: "wrap", gap: `${spacing(2)} ${spacing(5)}`, marginTop: spacing(6) }}>
        {TOC.map(([id, label]) => (
          <a key={id} href={`#${id}`}>
            {label}
          </a>
        ))}
      </nav>

      <Section id="what" title="What EduFadder is">
        <p>
          EduFadder is an educational technology that works as an annotation layer on the web, inside the Chrome browser. Once
          installed, it recognises well-known education ideas — we call them <strong>edufads</strong>, such as matching
          teaching to “learning styles” — on the pages you visit, and points you to a short summary of what research says,
          with the sources.
        </p>
        <p>
          Its purpose is <strong>awareness without absolute judgements</strong>. Some ideas it recognises are well supported
          by research (like practising recall), some are mixed, and some haven't held up. EduFadder tells you which, in plain
          language, and leaves the conclusion to you.
        </p>
        <img src="/media/edufadder-demo.gif" alt="Animation: EduFadder highlights an idea on a page and opens a research note." className="ef-animated" style={{ width: "100%", maxWidth: 760, borderRadius: radii.lg, border: `2px solid ${colors.border}`, margin: `${spacing(4)} 0` }} />
      </Section>

      <Section id="start" title="Getting started">
        <ol>
          <li>
            <Link to="/get-extension">Download and install the extension</Link> (about two minutes).
          </li>
          <li>That's all — EduFadder starts working as soon as it's installed. The welcome page explains what it does and how to turn it off.</li>
          <li>Pin EduFadder to your toolbar (puzzle-piece icon → pin).</li>
          <li>
            Visit the <Link to="/practice">practice page</Link> to see it work.
          </li>
        </ol>
        <p>No account is needed. Creating one is optional — it gives you a personal dashboard and lets you send feedback.</p>
      </Section>

      <Section id="browse" title="While you browse and read">
        <p>As you read, EduFadder checks each page automatically. When a passage mentions an edufad it knows:</p>
        <ul>
          <li>the phrase gets a <span style={{ background: "rgba(206,184,136,0.45)", textDecoration: `underline 2px ${colors.brand}`, padding: "0 2px" }}>soft gold highlight</span>;</li>
          <li>a small EduFadder marker appears beside it — a filled garnet marker when the page presents the idea as established, an outlined one when the page is discussing or questioning it;</li>
          <li>the toolbar icon shows how many ideas were noticed; and</li>
          <li>a short summary opens by itself in the bottom corner, listing each idea with where research stands. Close it any time; it stays closed on that page.</li>
        </ul>
        <p>
          You don't need to press anything. Select an idea in the summary, or click a highlight or marker, to read its full
          note. Most pages get nothing at all — EduFadder is designed to stay quiet unless it's confident.
        </p>
      </Section>

      <Section id="notes" title="Reading a research note">
        <p>Each note tells you:</p>
        <ul>
          <li><strong>What the page is doing</strong> — e.g. “This page presents this idea as established” or “This page questions or argues against this idea”.</li>
          <li><strong>Where research stands</strong>, as one of four labels:</li>
        </ul>
        <div style={{ display: "grid", gap: spacing(2), margin: `${spacing(2)} 0 ${spacing(4)}` }}>
          <div><Chip tone="green">Well supported by research</Chip> — consistent evidence that it helps.</div>
          <div><Chip tone="amber">Research is mixed</Chip> — effects are small, inconsistent, or depend on the situation.</div>
          <div><Chip tone="red">Not well supported by research so far</Chip> — studies haven't found the benefits claimed.</div>
          <div><Chip tone="neutral">Too little research to say yet</Chip> — not enough good studies either way.</div>
        </div>
        <ul>
          <li><strong>Worth knowing</strong> — the nuance: what's true about the idea, and where it goes too far.</li>
          <li><strong>A research-informed approach</strong> — something to try instead or alongside.</li>
          <li><strong>Sources</strong> — the full report on this site lists every citation, with links.</li>
        </ul>
      </Section>

      <Section id="search" title="While you search">
        <p>
          On Google, Bing, DuckDuckGo, Yahoo, Ecosia, Brave Search and Startpage, EduFadder adds research notes when your
          search — or the results — mention an edufad. If you search “learning styles quiz”, a note about learning styles
          appears in the corner, and matching result snippets are highlighted.
        </p>
        <p>
          <strong>This happens entirely on your device.</strong> Your searches and search results are never sent to EduFadder.
        </p>
      </Section>

      <Section id="anything" title="Optional extras">
        <p>None of these are needed for everyday use — automatic detection does the work. They're there for special cases.</p>
        <ul>
          <li><strong>Selected text:</strong> select a sentence or paragraph on any page, right-click, and choose <em>Check with EduFadder</em>.</li>
          <li><strong>This page, right now:</strong> click the toolbar icon and choose <em>Check this page anyway</em> — useful where automatic detection is paused or off.</li>
          <li><strong>Without the extension:</strong> paste text into <Link to="/check">Check a passage</Link>, or browse the <Link to="/edufads">edufads library</Link>.</li>
        </ul>
      </Section>

      <Section id="control" title="Staying in control">
        <ul>
          <li><strong>Pause on a site</strong> from the toolbar popup or the corner pill. Resume any time from the popup or Settings.</li>
          <li><strong>Snooze for an hour</strong> everywhere from the popup.</li>
          <li><strong>Hide the highlights</strong> (keep just the corner pill), or turn search notes off, in Settings.</li>
          <li><strong>Turn automatic detection off</strong> in Settings. EduFadder then reads nothing unless you ask it to check a page or a selection. To remove its access completely, use chrome://extensions → EduFadder → Site access, or remove the extension.</li>
        </ul>
      </Section>

      <Section id="privacy" title="Your privacy">
        <ul>
          <li>Pages are checked <strong>on your device first</strong> against a short list of phrases. Pages that don't mention a known idea are never sent anywhere.</li>
          <li>When a page does, only <strong>those passages</strong> are sent for a closer look — never the page address, your browsing history, or your searches.</li>
          <li>Passages are analysed in memory and <strong>not stored</strong>, and nothing you read is used to train any model.</li>
          <li>Without an account, EduFadder keeps no record of you at all. With one, your dashboard shows only your own activity.</li>
        </ul>
        <p>
          Full details: <Link to="/privacy">privacy</Link>.
        </p>
      </Section>

      <Section id="improve" title="Help improve EduFadder">
        <p>
          Every note asks “Was this note useful?”. Answers (from signed-in users) go to the curators who maintain the evidence,
          and <Link to="/suggest">suggestions</Link> for new edufads are researched one by one. When curators publish an update,
          every EduFadder user receives it automatically — so each report makes the tool better for the next reader.
        </p>
      </Section>

      <Section id="faq" title="Questions">
        <dl style={{ margin: 0 }}>
          <dt style={{ fontWeight: 700 }}>Is EduFadder saying the page is wrong?</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>No. It summarises research on an idea the page mentions. Pages discuss ideas for many reasons — including to criticise them.</dd>
          <dt style={{ fontWeight: 700 }}>Who decides what the research says?</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>Each entry is drafted by a researcher, checked by a different reviewer, and only then published, with its sources. See <Link to="/about">About</Link>.</dd>
          <dt style={{ fontWeight: 700 }}>Why didn't it flag something?</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>It only knows the ideas in the <Link to="/edufads">library</Link>, and it would rather miss a loosely worded mention than guess. <Link to="/suggest">Suggest</Link> what it should learn.</dd>
          <dt style={{ fontWeight: 700 }}>Does it work in other browsers?</dt>
          <dd style={{ margin: 0 }}>Chrome and Chromium-based browsers (Edge, Brave). Not Firefox or Safari yet.</dd>
        </dl>
      </Section>

      <p style={{ ...s.mutedText, marginTop: spacing(10), fontFamily: fonts.sans }}>
        Ready? <Link to="/get-extension">Get the extension</Link> · <Link to="/practice">Practice page</Link>
      </p>
    </div>
  );
}
